import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useSquadAgentDeleteControllerExecuteMutation,
  useCampaignAgentDeleteControllerExecuteMutation,
  AgentDto,
} from '@shared/services/apiService/apiService';
import { SQUAD_MESSAGES } from '../constants/squad.constants';
import { isErrorResponse } from '../utils/squadErrors';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';

export const useAgentOperations = (
  campaignId: string | undefined,
  squadId: string | null,
  agents: AgentDto[],
  onSuccess?: () => void,
) => {
  const { t } = useTranslation();
  const { showError } = useErrorHandler();

  const [deleteSquadAgent, { isLoading: isDeletingSquadAgent }] =
    useSquadAgentDeleteControllerExecuteMutation();
  const [deleteCampaignAgent, { isLoading: isDeletingCampaignAgent }] =
    useCampaignAgentDeleteControllerExecuteMutation();

  const deleteAgent = useCallback(
    async (agentId: string): Promise<{ success: boolean; newDefaultAgentId?: string }> => {
      if (!agentId) {
        showError({
          title: t('adminMainPage.errors.deleteAgentFailedTitle'),
          message: t('adminMainPage.errors.invalidAgentId'),
          suggestion: t('adminMainPage.errors.selectValidAgentSuggestion'),
        });
        return { success: false };
      }

      try {
        let response;
        let newDefaultAgentId: string | undefined;

        if (squadId) {
          response = await deleteSquadAgent({
            id: squadId,
            agentId,
          });

          if (isErrorResponse(response)) {
            showError({
              title: t('adminMainPage.errors.deleteAgentFailedTitle'),
              message: t(SQUAD_MESSAGES.AGENT_DELETED_ERROR),
              suggestion: t('adminMainPage.errors.deleteAgentSuggestion'),
            });
            return { success: false };
          }

          const data = response.data;
          if (data && 'defaultAgentId' in data && data.defaultAgentId) {
            newDefaultAgentId = data.defaultAgentId;
          }

          // Show success toast
          toast.success(CustomSuccessToast, {
            closeButton: false,
            autoClose: 5000,
            icon: false,
            data: {
              title: t('adminMainPage.squad.messages.success'),
              description: t(SQUAD_MESSAGES.AGENT_DELETED_SUCCESS),
            },
          });

          if (onSuccess) {
            onSuccess();
          }

          return { success: true, newDefaultAgentId };
        }

        response = await deleteCampaignAgent({
          id: campaignId || '',
          agentId,
        });

        if (isErrorResponse(response)) {
          showError({
            title: t('adminMainPage.errors.deleteAgentFailedTitle'),
            message: t('adminMainPage.errors.deleteAgentFailed'),
            suggestion: t('adminMainPage.errors.deleteAgentSuggestion'),
          });
          return { success: false };
        }

        // Show success toast
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('adminMainPage.squad.messages.success'),
            description: t('adminMainPage.agent.deleteSuccess'),
          },
        });

        if (onSuccess) {
          onSuccess();
        }

        return { success: true };
      } catch (error) {
        const errorMessage = squadId
          ? t(SQUAD_MESSAGES.AGENT_DELETED_ERROR)
          : t('adminMainPage.errors.deleteAgentFailed');

        showError({
          title: t('adminMainPage.errors.deleteAgentFailedTitle'),
          message: errorMessage,
          error,
          suggestion: t('adminMainPage.errors.deleteAgentSuggestion'),
        });

        return { success: false };
      }
    },
    [squadId, campaignId, agents, deleteSquadAgent, deleteCampaignAgent, t, onSuccess, showError],
  );

  return {
    deleteAgent,
    isDeleting: isDeletingSquadAgent || isDeletingCampaignAgent,
  };
};
